var FOCUS_CLASS = "tv-focused";

var FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [data-tv-focus]';

var lastFocused: HTMLElement | null = null;
var seenTraps: HTMLElement[] = [];

function hasClass(el: HTMLElement, name: string): boolean {
  var cls = el.className;
  if (!cls || typeof cls !== "string") {
    var attr = el.getAttribute("class");
    cls = attr || "";
  }
  if (!cls) return false;
  var parts = cls.split(" ");
  var i = 0;
  for (i = 0; i < parts.length; i += 1) {
    if (parts[i] === name) return true;
  }
  return false;
}

export function isShown(el: HTMLElement): boolean {
  if (!el) return false;
  if (el.getAttribute("aria-hidden") === "true") return false;
  if (el.getAttribute("disabled") != null) return false;
  var node: HTMLElement | null = el;
  while (node) {
    if (node.getAttribute && node.getAttribute("aria-hidden") === "true") return false;
    if (hasClass(node, "pointer-events-none")) return false;
    if (hasClass(node, "opacity-0")) return false;
    // کروم مخفی پلیر: فقط CSS pointer-events دارد، نه کلاس utility
    if (hasClass(node, "player-chrome--hidden")) {
      // برای ریموت قابل فوکوس می‌ماند؛ موس/لمس با CSS قطع است
      break;
    }
    node = node.parentElement;
  }
  if (el.offsetWidth <= 0 && el.offsetHeight <= 0) return false;
  return true;
}

function isFocusableCandidate(el: HTMLElement): boolean {
  if (!isShown(el)) return false;
  var tab = el.getAttribute("tabindex");
  if (tab === "-1") return false;
  var tag = el.tagName;
  if (tag === "INPUT") {
    var type = (el as HTMLInputElement).type;
    if (type && type.toLowerCase() === "hidden") return false;
    return true;
  }
  if (tag === "BUTTON" || tag === "SELECT" || tag === "TEXTAREA") return true;
  if (tag === "A") {
    var href = el.getAttribute("href");
    return !!href;
  }
  if (el.getAttribute("data-tv-focus") != null) return true;
  if (tab != null && tab !== "") return true;
  return false;
}

export function getFocusable(root: ParentNode): HTMLElement[] {
  var nodes = root.querySelectorAll(FOCUSABLE_SELECTOR);
  var out: HTMLElement[] = [];
  var i = 0;
  for (i = 0; i < nodes.length; i += 1) {
    var el = nodes[i] as HTMLElement;
    if (isFocusableCandidate(el)) out.push(el);
  }
  return out;
}

export function getTopTrap(): HTMLElement | null {
  var nodes = document.querySelectorAll("[data-tv-trap]");
  var i = nodes.length - 1;
  for (i = nodes.length - 1; i >= 0; i -= 1) {
    var el = nodes[i] as HTMLElement;
    if (isShown(el)) return el;
  }
  return null;
}

function closestFocusable(node: HTMLElement | null): HTMLElement | null {
  var current = node;
  while (current && current !== document.body) {
    if (isFocusableCandidate(current)) return current;
    current = current.parentElement;
  }
  return null;
}

export function syncFocusClass(el: HTMLElement | null): void {
  if (lastFocused && lastFocused !== el) {
    if (lastFocused.classList) {
      lastFocused.classList.remove(FOCUS_CLASS);
    }
  }
  lastFocused = el;
  if (el && el.classList) {
    el.classList.add(FOCUS_CLASS);
  }
}

function scrollFocusIntoView(el: HTMLElement): void {
  var marginX = 64;
  var marginY = 64;
  var rect = el.getBoundingClientRect();
  var viewW = window.innerWidth || document.documentElement.clientWidth;
  var viewH = window.innerHeight || document.documentElement.clientHeight;
  var dy = 0;

  if (rect.height >= viewH - marginY * 2) {
    dy = rect.top - marginY;
  } else if (rect.top < marginY) {
    dy = rect.top - marginY;
  } else if (rect.bottom > viewH - marginY) {
    dy = rect.bottom - (viewH - marginY);
  }

  if (dy) {
    var body = document.body;
    if (body && body.scrollHeight > viewH) {
      body.scrollTop = body.scrollTop + dy;
    } else {
      document.documentElement.scrollTop = document.documentElement.scrollTop + dy;
    }
  }

  var parent = el.parentElement;
  while (parent) {
    var style = window.getComputedStyle(parent);
    var overflowX = style.overflowX;
    var canScrollX = overflowX === "auto" || overflowX === "scroll";
    if (canScrollX && parent.scrollWidth > parent.clientWidth + 1) {
      var item = el.getBoundingClientRect();
      var box = parent.getBoundingClientRect();
      if (item.left < box.left + marginX) {
        parent.scrollLeft = parent.scrollLeft - (box.left + marginX - item.left);
      } else if (item.right > box.right - marginX) {
        parent.scrollLeft = parent.scrollLeft + (item.right - (box.right - marginX));
      }
    }
    parent = parent.parentElement;
  }
}

export function focusEl(el: HTMLElement, scroll: boolean): void {
  if (!el || !el.focus) return;
  el.focus();
  syncFocusClass(el);
  if (!scroll) return;
  scrollFocusIntoView(el);
}

function rectOf(el: HTMLElement): ClientRect {
  return el.getBoundingClientRect();
}

function overlapsCross(from: ClientRect, to: ClientRect, horizontal: boolean): boolean {
  if (horizontal) {
    return to.bottom > from.top && to.top < from.bottom;
  }
  return to.right > from.left && to.left < from.right;
}

function pickInDirection(
  current: HTMLElement,
  items: HTMLElement[],
  dir: string,
  strict: boolean
): HTMLElement | null {
  var from = rectOf(current);
  var fromCx = from.left + from.width / 2;
  var fromCy = from.top + from.height / 2;
  var best: HTMLElement | null = null;
  var bestScore = Infinity;
  var i = 0;
  for (i = 0; i < items.length; i += 1) {
    var item = items[i];
    if (item === current) continue;
    var rect = rectOf(item);
    if (rect.width <= 0 || rect.height <= 0) continue;
    var cx = rect.left + rect.width / 2;
    var cy = rect.top + rect.height / 2;
    var dx = cx - fromCx;
    var dy = cy - fromCy;
    var primary = 0;
    var secondary = 0;
    var horizontal = dir === "left" || dir === "right";
    if (dir === "left") {
      if (dx >= -1) continue;
      primary = -dx;
      secondary = Math.abs(dy);
    } else if (dir === "right") {
      if (dx <= 1) continue;
      primary = dx;
      secondary = Math.abs(dy);
    } else if (dir === "up") {
      if (dy >= -1) continue;
      primary = -dy;
      secondary = Math.abs(dx);
    } else if (dir === "down") {
      if (dy <= 1) continue;
      primary = dy;
      secondary = Math.abs(dx);
    } else {
      continue;
    }
    var overlap = overlapsCross(from, rect, horizontal);
    if (strict && !overlap && secondary > primary) continue;
    var score = primary + secondary * 2;
    if (!overlap) score += 10000;
    if (score < bestScore) {
      bestScore = score;
      best = item;
    }
  }
  return best;
}

export function moveSpatial(dir: string): boolean {
  var trap = getTopTrap();
  var root: ParentNode = trap || document.body;
  var items = getFocusable(root);
  if (!items.length) return false;

  var active = document.activeElement as HTMLElement | null;
  var current: HTMLElement | null = null;
  if (active && active.nodeType === 1) {
    if (items.indexOf(active) >= 0) {
      current = active;
    } else {
      current = closestFocusable(active);
      if (current && items.indexOf(current) < 0) current = null;
    }
  }

  if (!current) {
    var marked = trap ? trap.querySelector("[data-tv-initial]") : null;
    if (marked && isShown(marked as HTMLElement)) {
      focusEl(marked as HTMLElement, true);
      return true;
    }
    focusEl(items[0], true);
    return true;
  }

  var next = pickInDirection(current, items, dir, true);
  if (!next) next = pickInDirection(current, items, dir, false);
  if (!next) return false;
  focusEl(next, true);
  return true;
}

export function focusInitial(trap: HTMLElement): void {
  var active = document.activeElement as HTMLElement | null;
  if (active && active.nodeType === 1 && trap.contains(active) && active !== document.body) {
    syncFocusClass(closestFocusable(active) || active);
    return;
  }
  var marked = trap.querySelector("[data-tv-initial]");
  if (marked && isFocusableCandidate(marked as HTMLElement)) {
    focusEl(marked as HTMLElement, false);
    return;
  }
  var items = getFocusable(trap);
  if (items.length) focusEl(items[0], false);
}

export function clickTrapClose(trap: HTMLElement): boolean {
  var buttons = trap.querySelectorAll("[data-tv-close]");
  var i = 0;
  for (i = 0; i < buttons.length; i += 1) {
    var btn = buttons[i] as HTMLElement;
    if (isShown(btn)) {
      btn.click();
      return true;
    }
  }
  if (buttons.length > 0) {
    (buttons[0] as HTMLElement).click();
    return true;
  }
  return false;
}

export function focusPreferredEntry(): void {
  if (getTopTrap()) return;
  var preferred = document.querySelector("[data-tv-initial]");
  if (preferred) {
    var prefEl = preferred as HTMLElement;
    var target: HTMLElement | null = null;
    if (isFocusableCandidate(prefEl)) {
      target = prefEl;
    } else {
      var nested = getFocusable(preferred);
      if (nested.length) {
        target = nested[0];
      } else if (
        prefEl.getAttribute("data-tv-play") != null ||
        prefEl.tagName === "BUTTON" ||
        prefEl.tagName === "A"
      ) {
        // کنترل مخفی پلیر و موارد مشابه
        target = prefEl;
      }
    }
    if (target) {
      focusEl(target, true);
      return;
    }
  }
  var nav = document.querySelector(".site-header__nav");
  var items = nav ? getFocusable(nav) : [];
  if (!items.length) items = getFocusable(document.body);
  if (items.length) focusEl(items[0], false);
}

export function syncTraps(): void {
  var nodes = document.querySelectorAll("[data-tv-trap]");
  var visible: HTMLElement[] = [];
  var i = 0;
  for (i = 0; i < nodes.length; i += 1) {
    var el = nodes[i] as HTMLElement;
    if (isShown(el)) visible.push(el);
  }
  var fresh: HTMLElement | null = null;
  for (i = 0; i < visible.length; i += 1) {
    if (seenTraps.indexOf(visible[i]) < 0) fresh = visible[i];
  }
  seenTraps = visible;
  if (fresh) focusInitial(fresh);
}
